import { useLocation } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { useEffect, useState, type ReactNode } from 'react';

import Header from '../Header';
import Footer from '../Footer';
import OngoingEvaluations from '@/features/ongoingEvaluations/components/OngoingEvaluationsPanel';

import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';
import { APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';
import Toaster from '@/shared/ui/Toaster';

import type { OngoingEvaluation } from '@/types';

interface AppLayoutProps {
  header?: ReactNode;
  footer?: ReactNode;
  shouldUseMobileDocumentFlow?: (pathname: string) => boolean;
  shouldAwaitRouteLoadingSignal?: (pathname: string) => boolean;
  isContentLoading?: boolean;
  /** Forwarded to the ongoing-evaluations panel (see OngoingEvaluationsPanel). */
  getOngoingEvaluationViewResultsTo?: (
    evaluation: OngoingEvaluation,
  ) => string | null;
  onOngoingEvaluationViewResults?: (evaluation: OngoingEvaluation) => void;
  children: ReactNode;
}

const AppLayout = ({
  header = <Header />,
  footer = <Footer isNewsletterAvailable={false} />,
  shouldUseMobileDocumentFlow = () => false,
  shouldAwaitRouteLoadingSignal = () => false,
  isContentLoading = false,
  getOngoingEvaluationViewResultsTo,
  onOngoingEvaluationViewResults,
  children,
}: AppLayoutProps) => {
  const location = useLocation();
  const useMobileDocumentFlow = shouldUseMobileDocumentFlow(location.pathname);

  const [isRouteContentLoading, setIsRouteContentLoading] = useState(false);

  const { isOngoingEvaluationsOpen, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        isOngoingEvaluationsOpen: state.isOngoingEvaluationsOpen,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );
  const { clearToasts } = useToast(
    useShallow((state) => ({
      clearToasts: state.clearToasts,
    })),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteLoading = (
      event: Event,
    ) => {
      const customEvent = event as CustomEvent<{ loading?: boolean }>;
      setIsRouteContentLoading(Boolean(customEvent.detail?.loading));
    };

    window.addEventListener(
      APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT,
      handleRouteLoading as EventListener,
    );

    return () => {
      window.removeEventListener(
        APP_LAYOUT_MOBILE_ROUTE_LOADING_EVENT,
        handleRouteLoading as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    setIsRouteContentLoading(shouldAwaitRouteLoadingSignal(location.pathname));
  }, [location.pathname, shouldAwaitRouteLoadingSignal]);

  useEffect(() => {
    clearToasts();
  }, [clearToasts, location.pathname, location.search]);

  const isFooterSuppressedOnMobile =
    useMobileDocumentFlow && (isContentLoading || isRouteContentLoading);

  return (
    <div
      className={`h-full w-full flex flex-col relative ${
        useMobileDocumentFlow
          ? 'max-sm:h-auto max-sm:min-h-dvh max-sm:overflow-x-hidden'
          : 'max-sm:overflow-x-hidden'
      }`}
    >
      {header}
      <div
        className="flex flex-col gap-4 px-4 lg:pr-8 py-4 w-full min-h-0 flex-1"
      >
        {children}
        <OngoingEvaluations
          isOpen={isOngoingEvaluationsOpen}
          onClose={() => setIsOngoingEvaluationsOpen(false)}
          getViewResultsTo={getOngoingEvaluationViewResultsTo}
          onViewResults={onOngoingEvaluationViewResults}
        />
        <Toaster />
      </div>
      <div
        className={
          isFooterSuppressedOnMobile ? 'max-sm:hidden' : ''
        }
      >
        {footer}
      </div>
    </div>
  );
};

export default AppLayout;
